import { Box, FabButton, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { useTranslation } from '@shared/core';
import {
  StyledGiftBasketContentRoot,
  StyledHeaderMsgContainer,
  StyledOverflowMsgContainer,
  StyledProductRowBadge,
  StyledProductListContainer,
  StyledProductRowImage,
  StyledProductRowImageContainer,
  StyledSegmentedControllerContainer,
  StyledSegmentedControllerTab,
  styles,
  TextWithOverflow,
  StyledNavContainer,
  StyledZeroStateContainer,
  RegistryChecklistWrapper,
} from './GiftBasketContent.styles';
import { useGiftBasketContentController } from './GiftBasketContent.controller';
import { GIFT_TRACKER_CONFIG, LINE_ITEM_STATUS_CONFIG, MAX_ITEM_COUNT } from './GiftBasketContent.constants';
import { GiftBasketOverlayContentProps, ProductRowProps, SegmentedControllerProps, SegmentedControllerTabProps } from './GiftBasketContent.types';
import { dataAttr } from '@shared/utils/dom';
import { Cart, CheckOutline, Gift } from '@withjoy/joykit/icons';
import { RegistryChecklist } from '../../../RegistryChecklist';
import { renderTimes, SkeletonGroup, SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEnableChecklist } from '../../hooks';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';
import { CartCheckoutSteps } from '../../../ShoppingCart';
import { useShoppingCart } from '../../../ShoppingCart/state';
import { ShoppingCartTab } from '../ShoppingCartTab/ShoppingCartTab';

import { Page } from '../../../RegistryGiftCounts/telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import RegistryAdvisorMobile from '../../../RegistryGiftCounts/RegistryAdvisorMobile';

const ProductRow: React.FC<ProductRowProps> = ({ status, eyebrow, hed, dek, photoUrl }) => {
  const { t } = useTranslation('sharedComponents');

  const badgeTrans = t('registryGiftBasket', 'giftOverlay', 'trackedGiftBadges');
  const badgeConfig = status && LINE_ITEM_STATUS_CONFIG[status];
  return (
    <Flex
      alignItems="center"
      marginTop={5}
    >
      <StyledProductRowImageContainer __css={styles.productRowImageContainer}>
        <StyledProductRowImage
          __css={styles.productRowImage}
          style={{ mixBlendMode: 'multiply', backgroundImage: photoUrl ? `url('${photoUrl}')` : undefined }}
        />
        {badgeConfig && (
          <StyledProductRowBadge __css={styles.productRowStatusBadge}>
            <badgeConfig.Icon />
            <TextV2
              marginLeft={2}
              color={badgeConfig.color}
              typographyVariant={'label2'}
              fontWeight={600}
            >
              {badgeTrans[status]?.()}
            </TextV2>
          </StyledProductRowBadge>
        )}
      </StyledProductRowImageContainer>
      <SpacingStack
        paddingX={5}
        paddingY={3}
        spacing={1}
      >
        {eyebrow && (
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            {eyebrow}
          </TextV2>
        )}
        <TextWithOverflow
          typographyVariant="hed1"
          $maxNoLines={2}
        >
          {hed}
        </TextWithOverflow>
        <TextV2
          typographyVariant="label2"
          color="mono10"
        >
          {dek}
        </TextV2>
      </SpacingStack>
    </Flex>
  );
};

const SegmentedPill: React.FC<SegmentedControllerTabProps> = props => {
  const { selected, tabName, ...restProps } = props;
  return (
    <StyledSegmentedControllerTab
      as="button"
      data-tab={tabName}
      data-selected={dataAttr(selected)}
      __css={styles.segmentedControllerTab}
      {...restProps}
    />
  );
};

const SegmentedController: React.FC<SegmentedControllerProps> = props => {
  const enableChecklistInGiftBasketWidget = useEnableChecklist();
  const { t } = useTranslation('sharedComponents');
  const overlayTrans = t('registryGiftBasket', 'giftOverlay');
  const { currentTab, onTabClick, registryItemCount, registryOrderCount } = props;
  const giftListTabTitle = enableChecklistInGiftBasketWidget ? overlayTrans.giftList.tabLabelV2() : overlayTrans.giftList.tabLabel({ count: registryItemCount });
  const { isEnabled: isShoppingCartEnabled } = useRegistryShopping();
  return (
    <StyledSegmentedControllerContainer __css={styles.segmentedControllerContainer}>
      {enableChecklistInGiftBasketWidget ? (
        <SegmentedPill
          selected={currentTab === 'checklist'}
          tabName="checklist"
          onClick={onTabClick}
        >
          <CheckOutline marginRight={2} />
          <span>{overlayTrans.checklist.tabLabel()}</span>
        </SegmentedPill>
      ) : (
        <SegmentedPill
          selected={currentTab === 'giftTracker'}
          tabName="giftTracker"
          onClick={onTabClick}
        >
          {overlayTrans.giftTracker.tabLabel({ count: registryOrderCount })}
        </SegmentedPill>
      )}
      <SegmentedPill
        selected={currentTab === 'giftList'}
        tabName="giftList"
        onClick={onTabClick}
      >
        {enableChecklistInGiftBasketWidget ? <Gift marginRight={2} /> : null}
        {giftListTabTitle}
      </SegmentedPill>
      {isShoppingCartEnabled ? (
        <SegmentedPill
          selected={currentTab === 'shoppingCart'}
          tabName="shoppingCart"
          onClick={onTabClick}
        >
          <Cart marginRight={2} />
          <span>{overlayTrans.shoppingCart.tabLabel()}</span>
        </SegmentedPill>
      ) : null}
    </StyledSegmentedControllerContainer>
  );
};

export const GiftBasketContent: React.FC<GiftBasketOverlayContentProps> = props => {
  const { currentTab, onSegmentedTabClick, onVisitManageTabClick, totalRegistryItemsCount, totalTrackedGiftsCount, onCloseDrawer } = props;
  const { hasData, loading, productContainerRef, productRowDataProps, overflowCount } = useGiftBasketContentController(props);
  const enableChecklistInGiftBasketWidget = useEnableChecklist();
  const {
    state: { currentCheckoutStep },
  } = useShoppingCart();
  props.eventId;

  const isCheckoutInitiated = currentTab === 'shoppingCart' && currentCheckoutStep !== CartCheckoutSteps.Cart;

  const { t } = useTranslation('sharedComponents');
  const overlayTrans = t('registryGiftBasket', 'giftOverlay');
  const tabTranslations = overlayTrans[currentTab];
  const { eventHandle } = useEventInfo();

  const { FabBtnIcon, ZeroStateIcon } = GIFT_TRACKER_CONFIG[currentTab];

  const renderChecklist = () => (
    <RegistryChecklistWrapper __css={{ ...styles.registryChecklistWrapper }}>
      <RegistryChecklist
        eventId={props.eventId}
        onCloseDrawer={onCloseDrawer}
      />
    </RegistryChecklistWrapper>
  );

  const renderGiftList = () => (
    <Box
      marginTop={7}
      overflowY="scroll"
      height="100%"
    >
      {enableChecklistInGiftBasketWidget && currentTab === 'giftList' ? (
        <Box margin={5}>
          <RegistryAdvisorMobile
            page={Page.giftsDrawer}
            eventId={props?.eventId || ''}
            eventHandle={eventHandle || ''}
            onActionComplete={onCloseDrawer}
          />
        </Box>
      ) : null}
      <StyledHeaderMsgContainer __css={styles.headerMsgContainer}>
        <TextV2 typographyVariant={enableChecklistInGiftBasketWidget ? 'hed4' : 'hed1'}>{tabTranslations.headerMsg()}</TextV2>
      </StyledHeaderMsgContainer>
      <SkeletonGroup
        __css={enableChecklistInGiftBasketWidget ? styles.giftBasketContentDrawerRootSkeleton : styles.giftBasketContentRootSkeleton}
        isReady={!loading}
        height={hasData ? 'auto' : 'calc(100% - 145px)'}
        placeholder={
          <StyledProductListContainer
            __css={styles.productRowContainer}
            ref={productContainerRef}
          >
            <SpacingStack spacing={5}>
              {renderTimes(MAX_ITEM_COUNT, _ => (
                <Flex alignItems="center">
                  <SkeletonThumbnail
                    width={pxToRem(110)}
                    height={pxToRem(110)}
                    borderRadius={pxToRem(8)}
                    marginRight={6}
                  />
                  <SpacingStack spacing={1}>
                    <SkeletonText
                      width={pxToRem(150)}
                      height={pxToRem(21)}
                      rows={1}
                    />
                    <SkeletonText
                      width={pxToRem(44)}
                      height={pxToRem(21)}
                      rows={1}
                    />
                  </SpacingStack>
                </Flex>
              ))}
            </SpacingStack>
          </StyledProductListContainer>
        }
      >
        {hasData ? (
          <StyledProductListContainer
            __css={styles.productRowContainer}
            ref={productContainerRef}
            paddingRight={6}
            paddingLeft={6}
          >
            {productRowDataProps.map(({ key, ...productRowProps }) => {
              return (
                <ProductRow
                  key={key}
                  {...productRowProps}
                />
              );
            })}
            {overflowCount > 0 && (
              <StyledOverflowMsgContainer __css={styles.overflowMsgContainer}>
                <TextV2 typographyVariant="hed2">{tabTranslations.overflowMsg({ count: overflowCount })}</TextV2>
              </StyledOverflowMsgContainer>
            )}
          </StyledProductListContainer>
        ) : (
          <Box
            height={hasData ? 'auto' : '100%'}
            display="flex"
            alignItems="center"
          >
            <StyledZeroStateContainer
              __css={styles.zeroStateContainer}
              paddingRight={6}
              paddingLeft={6}
            >
              <ZeroStateIcon />
              <Box __css={styles.zeroStateMessageContainer}>
                <TextV2
                  typographyVariant="body1"
                  color="mono14"
                >
                  {tabTranslations.zeroStateMsg()}
                </TextV2>
              </Box>
            </StyledZeroStateContainer>
          </Box>
        )}
      </SkeletonGroup>
      <FabButton
        data-manage-tab={currentTab}
        position="absolute"
        bottom={7}
        left="50%"
        transform="translateX(-50%)"
        height={9}
        zIndex={1}
        startIcon={FabBtnIcon}
        onClick={onVisitManageTabClick}
      >
        {tabTranslations.fabBtn()}
      </FabButton>
    </Box>
  );

  const renderShoppingCart = () => (
    <ShoppingCartTab
      eventId={props.eventId}
      eventHandle={eventHandle || ''}
    />
  );

  const renderTabContent = () => {
    switch (currentTab) {
      case 'checklist':
        return renderChecklist();
      case 'giftList':
      case 'giftTracker':
        return renderGiftList();
      case 'shoppingCart':
        return renderShoppingCart();
    }
  };

  return (
    <StyledGiftBasketContentRoot
      height={hasData ? 'auto' : '100%'}
      __css={enableChecklistInGiftBasketWidget ? styles.giftBasketContentDrawerRoot : styles.giftBasketContentRoot}
    >
      {!isCheckoutInitiated && (
        <StyledNavContainer __css={styles.navContainer}>
          <SegmentedController
            onTabClick={onSegmentedTabClick}
            currentTab={currentTab}
            registryItemCount={totalRegistryItemsCount}
            registryOrderCount={totalTrackedGiftsCount}
          />
        </StyledNavContainer>
      )}
      {renderTabContent()}
    </StyledGiftBasketContentRoot>
  );
};
